// The sample day /demo and QA run on. Never stored, never sent anywhere.
//
// Built relative to "now" so it always has matches still to start, in the
// same shape The Odds API returns, so it goes through exactly the code a real
// day does: normaliseOdds, then the ticket search.

import { localDate } from './tickets.js';
import { tierFor } from './tier.js';

// The sample RACING card. It lives in its own file — a card of six races with
// fourteen books on each is longer than the whole sports day below — but it is
// re-exported here so `lib/demo.js` stays the one door the store knocks on for
// anything sample-shaped. See demo-racing.js for what is planted in it.
export { demoRaceCard, demoRaceFeatures, demoRaceMovers, DEMO_BOOKS } from './demo-racing.js';

/**
 * [home, away, sport, sportKey, minutes from now, home price, away price, draw price]
 *
 * Exported because the demo paper trail (`demo-trail.js`) is built from these
 * same fixtures: a trail naming clubs the demo board has never heard of would
 * print a History screen that does not belong to the Today screen above it.
 */
export const DAY = [
  ['Hawthorn', 'Essendon', 'AFL', 'aussierules_afl', -20, 1.18, 5.0],
  // THE NEXT THING TO START, eight minutes out. It used to be inside the live
  // buffer — a ticket carrying it was dealt EXPIRED, which was how /demo always
  // had something above the Now rule — and the buffer is zero now
  // (`LIVE_BUFFER_MS`), so a ticket on this one is live when the demo opens and
  // moves across the fold eight minutes later, struck through, the way every
  // other ticket in the app does. Hawthorn above has already started and the
  // deal cutoff never lets it onto a ticket at all, so a demo read in its first
  // eight minutes has an empty Past: that is the honest shape of a day nothing
  // has happened in yet.
  ['Western Bulldogs', 'North Melbourne', 'AFL', 'aussierules_afl', 8, 1.63, 2.25],
  ['Collingwood', 'Carlton', 'AFL', 'aussierules_afl', 25, 1.45, 2.8],
  ['Hobart Hurricanes', 'Sydney Sixers', 'Big Bash', 'cricket_big_bash', 50, 1.5, 2.6],
  ['Penrith Panthers', 'Dolphins', 'NRL', 'rugbyleague_nrl', 85, 1.3, 3.6],
  ['Brisbane Lions', 'Geelong Cats', 'AFL', 'aussierules_afl', 120, 1.62, 2.3],
  ['Melbourne Storm', 'Cronulla Sharks', 'NRL', 'rugbyleague_nrl', 150, 1.55, 2.45],
  ['Melbourne Victory', 'Western United', 'A-League', 'soccer_australia_aleague', 170, 1.42, 6.5, 4.4],
  ['Adelaide 36ers', 'Perth Wildcats', 'NBL', 'basketball_nbl', 200, 1.61, 2.35],
  ['Sydney Swans', 'GWS Giants', 'AFL', 'aussierules_afl', 240, 1.9, 1.9],
  ['Sydney Roosters', 'South Sydney Rabbitohs', 'NRL', 'rugbyleague_nrl', 250, 1.75, 2.1],
  ['Sydney FC', 'Wellington Phoenix', 'A-League', 'soccer_australia_aleague', 260, 1.95, 3.6, 3.7],
  ['NZ Warriors', 'Newcastle Knights', 'NRL', 'rugbyleague_nrl', 300, 2.2, 1.7],
  ['Canberra Raiders', 'Gold Coast Titans', 'NRL', 'rugbyleague_nrl', 330, 1.35, 3.2],
  ['Arsenal', 'Everton', 'EPL', 'soccer_epl', 420, 1.22, 12.0, 6.5],
  ['Chelsea', 'Brighton', 'EPL', 'soccer_epl', 480, 2.05, 3.4, 3.6],
  ['Liverpool', 'Aston Villa', 'EPL', 'soccer_epl', 520, 1.4, 6.0, 4.8],
];

/**
 * One bookmaker's h2h market, in The Odds API's own shape.
 *
 * `links` is `{ home, away }` → a URL, and it lands on the OUTCOME exactly
 * where `includeLinks=true` puts it upstream — which is what makes it a BETSLIP
 * link (see `linkOf`, server/providers/canonical.mjs). Only one book on one
 * match carries one, for the same reason every other plant on this board is one
 * of a kind: the demo has to show both the deep link and the front door.
 */
const book = (key, title, home, away, h, a, d, links = null, opens = null) => ({
  key,
  title,
  markets: [{
    key: 'h2h',
    outcomes: [
      { name: home, price: h, ...(links?.home ? { link: links.home } : {}) },
      {
        name: away,
        price: a,
        ...(links?.away ? { link: links.away } : {}),
        // WHAT THE BOARD OPENED AT (F1), where the server has kept every
        // reading of the day (`stampOpenings`, server/odds.mjs). One outcome on
        // this board carries one, for the same reason one carries a betslip
        // link: the demo has to show the screen that prints it and the screens
        // that do not.
        ...(opens?.away ? { openingPrice: opens.away.price, openingAt: opens.away.at } : {}),
      },
      ...(d ? [{ name: 'Draw', price: d }] : []),
    ],
  }],
});

// TAB a touch shorter on the favourite, so switching bookmaker visibly moves prices.
const shorter = (p) => Math.max(1.01, Math.round((p - 0.04) * 100) / 100);

/**
 * The two fixtures the detector has something to say about, keyed by the home
 * side so the DAY table above stays the one list of what is on.
 *
 * A board of two bookmakers can never flag anything — `MIN_BOOKS` is three, and
 * for good reason — so these matches carry the books a real Saturday carries.
 * Nothing else on the demo board is touched: Sportsbet's and TAB's prices are
 * the same ones the trail, the history and Classic's own fixture were built
 * against, and the extra shops are added beside them.
 *
 *   Collingwood  one book out of line, and nothing else: Ladbrokes is paying
 *                3.20 on Carlton where the rest of the board makes it ~2.80.
 *                Back the other side at the board's best and the round still
 *                adds to more than one, so this is an outlier and NOT covered.
 *   Storm        the covered one: Ladbrokes long on Storm, Neds long on
 *                Cronulla, and 1/1.62 + 1/2.70 comes to less than a whole.
 *
 * [key, title, home price, away price]
 */
/**
 * THE ONE MATCH LEFT TOO THIN TO HAVE A FAIR PRICE, on purpose.
 *
 * `board-view.js` refuses to print a fair price or a percentage on a match
 * fewer than `MIN_BOOKS` shops have priced (engine review #1) — the figure
 * there used to be a book's own price measured against itself. That refusal is
 * a screen a punter will meet on a country meeting or an obscure league, so the
 * demo carries one: Hobart is quoted by Sportsbet alone, and its cells print a
 * dash and "1 book" where the rest of the board prints numbers.
 */
const ONE_BOOK_MATCH = 'Hobart Hurricanes';

/**
 * A REAL SATURDAY IS NOT TWO SHOPS, and a board of two has no market on it at
 * all: `MIN_BOOKS` is three, so with Sportsbet and TAB alone every row on the
 * Matches tab would print "few books" and no figure — true of this fixture and
 * false of every real board, which carries eight to thirteen.
 *
 * Betr is the third, quoting exactly what Sportsbet quotes: it adds the vote
 * the market needs without moving a single best price (a tie goes to the first
 * book listed) and without flagging anything, so every existing demo id, price,
 * ticket and trail link is untouched. The two matches the detector is meant to
 * find carry their own real shops below.
 */
const THIRD_BOOK = ['betr_au', 'Betr'];

const EXTRA_BOOKS = {
  Collingwood: [
    ['neds', 'Neds', 1.45, 2.8],
    // The mispriced one, and the only outcome on this board with a link on it:
    // Ladbrokes' Carlton is what Mispriced odds prints as a single naming
    // Ladbrokes, so it is the leg whose button can say "Add to Ladbrokes slip".
    // It is also the one price on the demo board with an OPENING behind it: it
    // opened at 3.05 this morning and is 3.20 now, which is the "since open
    // +4.9%" line on that ticket's detail rows.
    ['ladbrokes_au', 'Ladbrokes', 1.45, 3.2, { away: 'https://www.ladbrokes.com.au/add-to-betslip?propositionId=demo-carlton-h2h' }, 3.05],
  ],
  'Melbourne Storm': [
    ['neds', 'Neds', 1.55, 2.7],
    ['ladbrokes_au', 'Ladbrokes', 1.62, 2.3],
  ],
};

/**
 * A day of odds. `seed` is the QA fixture: `empty` has no markets, and
 * `default` is fetched three hours ago so the STALE stamp can be photographed.
 *
 * `sample` is the REAL APP asking, not /demo — the board Today prints before
 * anybody has a key (`sampleMode` in store.js). It is the same day, deliberately:
 * a second set of fixtures would be a second thing to keep true, and what the
 * banner over it promises is "yesterday's shape, today's date", which is exactly
 * what this table has always been. It takes the ordinary six-minute stamp, and
 * the store hides the prices bar over it rather than print a fetch time nobody
 * paid for.
 */
export function demoSnapshot(nowMs = Date.now(), seed = null) {
  const forDate = localDate(nowMs);
  const fetchedAt = new Date(nowMs - (seed === 'default' ? 180 : 6) * 60_000).toISOString();
  // When the board opened: the first capture of the day, six hours back.
  const openedAt = new Date(nowMs - 6 * 60 * 60_000).toISOString();
  if (seed === 'empty') return { forDate, fetchedAt, events: [] };
  return {
    forDate,
    fetchedAt,
    events: DAY.map(([home, away, title, key, mins, h, a, d], i) => ({
      id: `demo-${i}`,
      sport_key: key,
      sport_title: title,
      commence_time: new Date(nowMs + mins * 60_000).toISOString(),
      home_team: home,
      away_team: away,
      bookmakers: home === ONE_BOOK_MATCH ? [
        book('sportsbet', 'SportsBet', home, away, h, a, d),
      ] : [
        book('sportsbet', 'SportsBet', home, away, h, a, d),
        book('tab', 'TAB', home, away, h < a ? shorter(h) : h, a < h ? shorter(a) : a, d),
        book(THIRD_BOOK[0], THIRD_BOOK[1], home, away, h, a, d),
        ...(EXTRA_BOOKS[home] ?? []).map(([key, title, eh, ea, links, open]) =>
          book(key, title, home, away, eh, ea, d, links,
            open ? { away: { price: open, at: openedAt } } : null)),
      ],
    })),
  };
}

/**
 * Which strategy printed a past ticket. The id is what History groups and
 * filters on and what the proposed→placed join matches; the label is what a
 * ticket printed by a strategy since deleted still calls itself.
 */
const CLASSIC = { strategyId: 'classic-four-ways', label: 'Classic Four Ways' };
const ELO = { strategyId: 'elo-edge', label: 'Elo Edge' };
const DIXON = { strategyId: 'dixon-coles-edge', label: 'Dixon-Coles Edge' };

/**
 * A single, at the one book that is out of line.
 *
 * Mispriced odds is the only built-in whose leg rule is `singles`, and a single
 * is a DIFFERENT INSTRUCTION from a multi: it names the book rather than saying
 * "Best price", because the whole of the bet is "this shop is paying over the
 * odds and the rest of the board is not". `strategy.js` prints exactly that —
 * `bookmaker` the book's display name, `bookmakerKey` beside it so nothing has
 * to match a string back — and the demo carries both or the one ticket shape
 * the app can print that is not a multi is never seen on /demo.
 */
const SINGLE = (bookmaker, bookmakerKey) =>
  ({ strategyId: 'mispriced-odds', label: 'Mispriced odds', bookmaker, bookmakerKey });

/**
 * Tickets placed on earlier days, for History.
 * [days ago, tier, result, legs, strategy, hand-marked legs?]
 *
 * FOUR MONTHS OF THEM, the same four months the paper trail runs over. A month
 * of twelve was enough for the 7d/30d split and nothing further: "All" was the
 * same window as "30d", the thirty-day delta had no previous thirty days behind
 * it to be a delta against, and a curve through a month of a dozen points is a
 * line with a couple of kinks in it. Forty-five over a hundred and twenty days
 * is a plausible season for somebody putting ten dollars on a few tickets a
 * week, and every window Insights offers reads differently.
 *
 * The mix is deliberate, and it is the shape of a real season rather than a
 * flattering one:
 *
 *   MOSTLY CLASSIC across all four tiers, because that is what the pad prints.
 *   A handful from `elo-edge` and `dixon-coles-edge` — the ranked picks, backed
 *   because they ranked — so By strategy has rows to sort against each other.
 *   TWO SINGLES from `mispriced-odds`, which is the one built-in that prints a
 *   one-leg ticket at a named book rather than a multi at "Best price".
 *   Two still open: one pending on a game to come, one whose games were two
 *   days ago and never got a score — "No result", which is a different row from
 *   "Pending" and has to be on the screen for the vocabulary to be complete.
 *   Two voids, a dozen-odd winners, and a losing majority.
 *
 * AND IT NETS OUT SLIGHTLY DOWN, on purpose. The paper trail over the same four
 * months is ahead; the money is behind. That gap is the most useful thing on
 * the Insights screen — Proposed and Placed answer different questions, and a
 * demo where both say the same thing teaches that they are the same number.
 */
const PAST = [
  [1, 'high', 'pending', [
    ['Carlton', 'Richmond', 'AFL', 2.4],
    ['Dolphins', 'Brisbane Broncos', 'NRL', 2.9],
    ['Western United', 'Adelaide United', 'A-League', 2.2],
    ['Perth Wildcats', 'Sydney Kings', 'NBL', 1.8],
  ], CLASSIC],
  [1, 'low', 'won', [
    ['Penrith Panthers', 'Parramatta Eels', 'NRL', 1.32],
    ['Collingwood', 'North Melbourne', 'AFL', 1.28],
  ], CLASSIC],
  // Proposed on the pad first, then backed — `demo-trail.js` reuses this
  // ticket's id, which is what gives the proposed→placed join something to
  // find on /demo.
  [2, 'low', 'won', [
    ['Melbourne Storm', 'Canberra Raiders', 'NRL', 1.35],
    ['Sydney Swans', 'Essendon', 'AFL', 1.3],
  ], CLASSIC],
  [3, 'medium', 'lost', [
    ['Brisbane Lions', 'Fremantle', 'AFL', 1.6],
    ['Melbourne Storm', 'Wests Tigers', 'NRL', 1.45],
    ['Sydney FC', 'Macarthur FC', 'A-League', 1.95],
  ], CLASSIC],
  [4, 'medium', 'void', [
    ['Chelsea', 'Brighton', 'EPL', 2.05],
    ['Liverpool', 'Aston Villa', 'EPL', 1.4],
  ], CLASSIC],
  // The second ticket the trail proposed and this history placed.
  [6, 'medium', 'won', [
    ['Hawthorn', 'North Melbourne', 'AFL', 1.55],
    ['Penrith Panthers', 'Gold Coast Titans', 'NRL', 1.4],
    ['Adelaide 36ers', 'Cairns Taipans', 'NBL', 1.62],
  ], ELO],
  [5, 'outrageous', 'lost', [
    ['Gold Coast Suns', 'Sydney Swans', 'AFL', 3.1],
    ['Gold Coast Titans', 'Sydney Roosters', 'NRL', 3.4],
    ['Newcastle Jets', 'Melbourne City', 'A-League', 4.2],
    ['Cairns Taipans', 'Melbourne United', 'NBL', 2.9],
    ['Everton', 'Manchester City', 'EPL', 8.5],
    ['Western Bulldogs', 'Geelong Cats', 'AFL', 2.7],
  ], CLASSIC],
  [9, 'low', 'lost', [
    ['Hobart Hurricanes', 'Melbourne Renegades', 'Big Bash', 1.38],
    ['Arsenal', 'Everton', 'EPL', 1.22],
  ], CLASSIC],
  [12, 'high', 'lost', [
    ['NZ Warriors', 'Newcastle Knights', 'NRL', 2.2],
    ['Geelong Cats', 'Port Adelaide', 'AFL', 1.95],
    ['Wellington Phoenix', 'Perth Glory', 'A-League', 2.35],
    ['Sydney Sixers', 'Perth Scorchers', 'Big Bash', 2.1],
  ], CLASSIC],
  [16, 'low', 'won', [
    ['Collingwood', 'Carlton', 'AFL', 1.45],
    ['Penrith Panthers', 'Dolphins', 'NRL', 1.3],
  ], CLASSIC],
  [21, 'medium', 'lost', [
    ['Brisbane Lions', 'Geelong Cats', 'AFL', 1.62],
    ['Melbourne Storm', 'Cronulla Sharks', 'NRL', 1.55],
    ['Perth Wildcats', 'Adelaide 36ers', 'NBL', 1.72],
  ], ELO],
  [27, 'outrageous', 'lost', [
    ['Essendon', 'Hawthorn', 'AFL', 3.4],
    ['Wests Tigers', 'Penrith Panthers', 'NRL', 5.5],
    ['Newcastle Jets', 'Sydney FC', 'A-League', 3.8],
    ['Everton', 'Liverpool', 'EPL', 6.5],
    ['Melbourne United', 'Perth Wildcats', 'NBL', 2.6],
  ], CLASSIC],

  // THE OTHER LOOSE END. Placed, its games kicked off two days ago, and no
  // score ever came back — which History reads as "No result" rather than
  // "Pending", and which is the row that says the auto-settle is not magic.
  [2, 'high', 'pending', [
    ['Geelong Cats', 'St Kilda', 'AFL', 1.5],
    ['Parramatta Eels', 'Wests Tigers', 'NRL', 1.55],
    ['Sydney Kings', 'Tasmania JackJumpers', 'NBL', 1.7],
    ['Melbourne City', 'Perth Glory', 'A-League', 1.65],
  ], CLASSIC],

  [7, 'low', 'lost', [
    ['Brisbane Broncos', 'Manly Sea Eagles', 'NRL', 1.42],
    ['Port Adelaide', 'West Coast Eagles', 'AFL', 1.25],
  ], CLASSIC],
  // MARKED BY HAND. The bookmaker's feed never carried these, so somebody
  // tapped each leg themselves — `resultSource: 'manual'`, which is what stops
  // the auto-settle ever overwriting a call a person made.
  [8, 'medium', 'won', [
    ['Adelaide Crows', 'North Melbourne', 'AFL', 1.5],
    ['Canberra Raiders', 'Newcastle Knights', 'NRL', 1.45],
    ['Melbourne United', 'Illawarra Hawks', 'NBL', 1.6],
  ], CLASSIC, ['won', 'won', 'won']],
  [10, 'high', 'lost', [
    ['Fremantle', 'Richmond', 'AFL', 1.38],
    ['Cronulla Sharks', 'Canterbury Bulldogs', 'NRL', 1.5],
    ['Brisbane Roar', 'Western United', 'A-League', 2.1],
    ['Perth Scorchers', 'Adelaide Strikers', 'Big Bash', 1.75],
  ], ELO],
  // Proposed on the pad first, then backed. See `TRAIL_LINKED`.
  [11, 'high', 'lost', [
    ['Western Bulldogs', 'Gold Coast Suns', 'AFL', 1.6],
    ['Penrith Panthers', 'St George Illawarra', 'NRL', 1.5],
    ['Arsenal', 'Wolves', 'EPL', 1.45],
    ['Adelaide 36ers', 'Brisbane Bullets', 'NBL', 1.35],
  ], CLASSIC],
  // A SINGLE. One leg, at the one shop paying over the odds — the ticket
  // `mispriced-odds` prints, and the only kind the app prints that is not a
  // multi.
  [13, 'low', 'won', [
    ['Hobart Hurricanes', 'Brisbane Heat', 'Big Bash', 1.95],
  ], SINGLE('Ladbrokes', 'ladbrokes_au')],
  [14, 'medium', 'lost', [
    ['Chelsea', 'Nottingham Forest', 'EPL', 1.55],
    ['Liverpool', 'Crystal Palace', 'EPL', 1.35],
    ['Arsenal', 'Brentford', 'EPL', 1.4],
  ], DIXON],
  [15, 'outrageous', 'lost', [
    ['North Melbourne', 'Collingwood', 'AFL', 4.2],
    ['Gold Coast Titans', 'Melbourne Storm', 'NRL', 4.8],
    ['Perth Glory', 'Melbourne Victory', 'A-League', 3.3],
    ['Brisbane Bullets', 'Sydney Kings', 'NBL', 2.8],
    ['Brighton', 'Manchester City', 'EPL', 5.0],
  ], CLASSIC],
  [17, 'medium', 'lost', [
    ['Carlton', 'Melbourne', 'AFL', 1.65],
    ['South Sydney Rabbitohs', 'Dolphins', 'NRL', 1.72],
    ['Sydney FC', 'Newcastle Jets', 'A-League', 1.55],
  ], CLASSIC, ['won', 'lost', null]],
  [18, 'low', 'won', [
    ['Melbourne Storm', 'Wests Tigers', 'NRL', 1.3],
    ['Collingwood', 'Essendon', 'AFL', 1.35],
  ], CLASSIC],
  [19, 'high', 'lost', [
    ['Sydney Swans', 'Hawthorn', 'AFL', 1.55],
    ['Sydney Roosters', 'Gold Coast Titans', 'NRL', 1.4],
    ['Sydney Sixers', 'Melbourne Stars', 'Big Bash', 1.65],
    ['Melbourne Victory', 'Macarthur FC', 'A-League', 1.8],
  ], CLASSIC],
  [20, 'low', 'lost', [
    ['Brisbane Lions', 'West Coast Eagles', 'AFL', 1.2],
    ['Penrith Panthers', 'Newcastle Knights', 'NRL', 1.28],
  ], ELO],
  [22, 'high', 'lost', [
    ['Geelong Cats', 'Adelaide Crows', 'AFL', 1.45],
    ['Canberra Raiders', 'Parramatta Eels', 'NRL', 1.6],
    ['Perth Wildcats', 'Cairns Taipans', 'NBL', 1.5],
    ['Liverpool', 'Everton', 'EPL', 1.55],
  ], CLASSIC],
  [23, 'medium', 'won', [
    ['Port Adelaide', 'North Melbourne', 'AFL', 1.62],
    ['Melbourne Storm', 'Dolphins', 'NRL', 1.5],
    ['Sydney Kings', 'Illawarra Hawks', 'NBL', 1.4],
  ], ELO],
  [25, 'high', 'lost', [
    ['Richmond', 'Gold Coast Suns', 'AFL', 1.75],
    ['Manly Sea Eagles', 'Canterbury Bulldogs', 'NRL', 1.58],
    ['Adelaide United', 'Wellington Phoenix', 'A-League', 1.9],
    ['Adelaide Strikers', 'Melbourne Renegades', 'Big Bash', 1.85],
  ], CLASSIC],
  // The pad proposed this one too.
  [26, 'medium', 'lost', [
    ['Hawthorn', 'Fremantle', 'AFL', 1.55],
    ['Brisbane Broncos', 'Wests Tigers', 'NRL', 1.32],
    ['Melbourne City', 'Brisbane Roar', 'A-League', 1.68],
  ], CLASSIC],
  [28, 'low', 'won', [
    ['Collingwood', 'West Coast Eagles', 'AFL', 1.28],
    ['Melbourne Storm', 'Newcastle Knights', 'NRL', 1.32],
  ], CLASSIC],
  [30, 'medium', 'void', [
    ['Manchester City', 'Southampton', 'EPL', 1.25],
    ['Arsenal', 'Ipswich Town', 'EPL', 1.3],
    ['Liverpool', 'Leicester City', 'EPL', 1.28],
  ], DIXON],
  [32, 'outrageous', 'lost', [
    ['St Kilda', 'Brisbane Lions', 'AFL', 3.6],
    ['Newcastle Knights', 'Penrith Panthers', 'NRL', 4.4],
    ['Cairns Taipans', 'Sydney Kings', 'NBL', 3.1],
    ['Wellington Phoenix', 'Melbourne City', 'A-League', 3.9],
    ['Melbourne Renegades', 'Perth Scorchers', 'Big Bash', 2.4],
    ['Everton', 'Arsenal', 'EPL', 5.5],
  ], CLASSIC],
  [36, 'high', 'lost', [
    ['Melbourne', 'Western Bulldogs', 'AFL', 1.68],
    ['Dolphins', 'Gold Coast Titans', 'NRL', 1.55],
    ['Tasmania JackJumpers', 'Brisbane Bullets', 'NBL', 1.6],
    ['Chelsea', 'Fulham', 'EPL', 1.7],
  ], CLASSIC],
  [38, 'medium', 'lost', [
    ['Sydney Swans', 'Richmond', 'AFL', 1.35],
    ['Sydney Roosters', 'Wests Tigers', 'NRL', 1.4],
    ['Perth Wildcats', 'Illawarra Hawks', 'NBL', 1.48],
  ], ELO],
  // And this one, which the pad ranked before it was backed.
  [39, 'high', 'won', [
    ['Brisbane Lions', 'North Melbourne', 'AFL', 1.55],
    ['Penrith Panthers', 'Parramatta Eels', 'NRL', 1.45],
    ['Melbourne Victory', 'Perth Glory', 'A-League', 1.5],
    ['Adelaide 36ers', 'Cairns Taipans', 'NBL', 1.4],
  ], CLASSIC],
  [45, 'medium', 'lost', [
    ['Geelong Cats', 'Essendon', 'AFL', 1.45],
    ['Canberra Raiders', 'Gold Coast Titans', 'NRL', 1.38],
    ['Sydney FC', 'Adelaide United', 'A-League', 1.72],
  ], CLASSIC, [null, 'lost', null]],
  [49, 'low', 'won', [
    ['Collingwood', 'Fremantle', 'AFL', 1.35],
    ['Melbourne Storm', 'Manly Sea Eagles', 'NRL', 1.3],
  ], CLASSIC],
  [53, 'outrageous', 'lost', [
    ['West Coast Eagles', 'Carlton', 'AFL', 3.9],
    ['Wests Tigers', 'Melbourne Storm', 'NRL', 6.0],
    ['Brisbane Roar', 'Sydney FC', 'A-League', 3.4],
    ['Illawarra Hawks', 'Melbourne United', 'NBL', 2.9],
    ['Brighton', 'Liverpool', 'EPL', 4.6],
  ], CLASSIC],
  // The pad proposed this one first as well.
  [58, 'low', 'lost', [
    ['Hawthorn', 'Gold Coast Suns', 'AFL', 1.3],
    ['Sydney Roosters', 'Newcastle Knights', 'NRL', 1.4],
  ], CLASSIC],
  [63, 'medium', 'won', [
    ['Port Adelaide', 'Richmond', 'AFL', 1.5],
    ['Brisbane Broncos', 'Dolphins', 'NRL', 1.45],
    ['Perth Wildcats', 'Brisbane Bullets', 'NBL', 1.62],
  ], ELO],
  [70, 'high', 'lost', [
    ['Western Bulldogs', 'Adelaide Crows', 'AFL', 1.6],
    ['Cronulla Sharks', 'St George Illawarra', 'NRL', 1.5],
    ['Hobart Hurricanes', 'Sydney Thunder', 'Big Bash', 1.8],
    ['Melbourne City', 'Newcastle Jets', 'A-League', 1.55],
  ], CLASSIC],
  // The second single, at a different shop.
  [74, 'low', 'won', [
    ['Sydney Thunder', 'Melbourne Stars', 'Big Bash', 2.3],
  ], SINGLE('Neds', 'neds')],
  // The oldest of the linked tickets: four months back, and the funnel still
  // finds it on All.
  [83, 'medium', 'lost', [
    ['Carlton', 'St Kilda', 'AFL', 1.58],
    ['Manly Sea Eagles', 'Wests Tigers', 'NRL', 1.35],
    ['Wellington Phoenix', 'Western United', 'A-League', 1.75],
  ], CLASSIC],
  [99, 'high', 'won', [
    ['Collingwood', 'North Melbourne', 'AFL', 1.45],
    ['Penrith Panthers', 'Gold Coast Titans', 'NRL', 1.6],
    ['Melbourne United', 'Cairns Taipans', 'NBL', 1.5],
    ['Arsenal', 'Everton', 'EPL', 1.35],
  ], CLASSIC],
  [113, 'low', 'lost', [
    ['Brisbane Lions', 'Essendon', 'AFL', 1.32],
    ['Melbourne Storm', 'Canterbury Bulldogs', 'NRL', 1.28],
  ], CLASSIC],
  [118, 'outrageous', 'lost', [
    ['Essendon', 'Sydney Swans', 'AFL', 3.3],
    ['Gold Coast Titans', 'Penrith Panthers', 'NRL', 5.0],
    ['Perth Glory', 'Melbourne City', 'A-League', 3.6],
    ['Cairns Taipans', 'Perth Wildcats', 'NBL', 2.7],
    ['Everton', 'Chelsea', 'EPL', 4.2],
  ], CLASSIC],
];

/**
 * The id a demo bet placed `daysAgo` days ago on `tier` carries.
 *
 * The one definition, because two things now have to agree on it: the history
 * rows below, and the paper trail that proposed a couple of those tickets the
 * morning before they were placed. Device-local days, as the rows themselves
 * are — the trail matches these ids to its own days rather than recomputing
 * them, so there is nothing here for a timezone to come between.
 */
export function demoMultiId(daysAgo, tier, nowMs = Date.now()) {
  return `demo-${localDate(new Date(nowMs - daysAgo * 86_400_000))}-${tier}`;
}

/**
 * The placed tickets the demo paper trail also proposed: [days ago, tier].
 *
 * EIGHT of them, spread across the four months rather than bunched in the first
 * week, because the funnel is drawn over whichever window the reader picked —
 * two in the last week is a middle band that empties the moment anybody taps
 * 30d or All. Each pair alternates a Classic set with a ranked pick (see the
 * join in `demo-trail.js`), and each is on a morning the pad actually printed
 * that tier, which is why none of them lands on a blank or half-board day.
 */
export const TRAIL_LINKED = [
  [2, 'low'], [6, 'medium'], [11, 'high'], [18, 'low'],
  [26, 'medium'], [39, 'high'], [58, 'low'], [83, 'medium'],
];

const cents = (n) => Math.round(n * 100) / 100;

/** History rows (multi, leg, bet-record), tagged with `entity` like stored rows are. */
export function demoHistoryRows(nowMs = Date.now()) {
  const rows = [];
  for (const [daysAgo, design, result, picks, strategy, marks] of PAST) {
    const base = new Date(nowMs - daysAgo * 86_400_000);
    const at = (h, m = 0) => new Date(base.getFullYear(), base.getMonth(), base.getDate(), h, m).toISOString();
    const forDate = localDate(base);
    const id = demoMultiId(daysAgo, design, nowMs);
    const { strategyId, label, bookmaker = 'Sportsbet', bookmakerKey = null } = strategy;
    const combinedOdds = cents(picks.reduce((acc, p) => acc * p[3], 1));
    const combinedProbability = picks.reduce((acc, p) => acc / p[3], 1);
    const potentialReturn = cents(10 * combinedOdds);
    rows.push({
      id,
      entity: 'multi',
      // The design it was cut to, and the risk its own legs earn — the two
      // fields a real ticket carries. The demo wrote the word by hand before
      // the two were split, which made it the one place in the app where a
      // "Low risk" ticket was low risk because somebody typed it.
      design,
      tier: tierFor(combinedProbability),
      forDate,
      // What printed it. `bet-record.strategyId` is not to be trusted (the
      // migration stamped every old bet Classic), so History and Insights both
      // read the strategy off the frozen multi — which means the demo has to
      // carry it here or every demo bet groups as Classic.
      strategyId,
      label,
      status: 'placed',
      stake: 10,
      // A multi says "Best price" or the account's own book; a single says
      // which shop, by name, and carries the key beside it. Same two fields
      // `strategy.js` prints and `markPlaced` copies onto the bet record.
      bookmaker,
      ...(bookmakerKey ? { bookmakerKey } : {}),
      oddsFetchedAt: at(9, 12),
      combinedOdds,
      combinedProbability,
      potentialReturn,
    });
    picks.forEach(([team, opp, sport, price], i) => rows.push({
      id: `${id}-${i}`,
      entity: 'leg',
      multiId: id,
      eventId: `${id}-e${i}`,
      selection: `${team} to win`,
      match: `${team} v ${opp}`,
      sportTitle: sport,
      commenceTime: at(13 + i, 10),
      price,
      impliedProbability: 1 / price,
      ...(bookmakerKey ? { bookmakerKey } : {}),
      // A leg somebody marked themselves. `resolveLeg` hands a manual leg
      // straight back untouched, so this is a call the auto-settle can never
      // overwrite — and the ticket says so on the screen.
      ...(marks?.[i] ? { result: marks[i], resultSource: 'manual', settledAt: at(22, 5) } : {}),
    }));
    rows.push({
      id: `bet-${id}`,
      entity: 'bet-record',
      multiId: id,
      placedAt: at(9, 40),
      stake: 10,
      bookmaker,
      bookmakerKey,
      result,
      // WHERE THE RESULT CAME FROM, which History now prints under every
      // settled copy ("Settled from scores" / "Settled by you"). A hand-marked
      // demo bet is the punter's own call; everything else in the invented
      // history settled off the scores, which is what the real auto-settle
      // stamps and what the demo has to say if the line is to be worth reading.
      ...(marks ? { resultSource: 'manual' } : result === 'pending' ? {} : { resultSource: 'auto' }),
      // A void multi hands the stake back — it is not a loss and it is not an
      // unknown, which are the only two things the other branches can say.
      returned: result === 'won' ? potentialReturn : result === 'lost' ? 0 : result === 'void' ? 10 : null,
      settledAt: result === 'pending' ? null : at(22, 5),
    });
  }
  return rows;
}
